import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { beginContextSelection, session } from '../session.js'
import { useI18n } from '../i18n.js'
import {
  createControllerViewMemory,
  getControllerDevices,
  getNetworkControllerSummaries,
  getControllerSummary,
  selectControllerContext,
  normalizeControllerFilters,
  normalizeDeviceFilters,
  deviceSearchQuery,
} from './controllers.js'

const memory = createControllerViewMemory()
const loadedCounts = new Map()
const denied = (error) => [401, 403, 404].includes(error?.status)
export const controllerConnection = (value, translate) => {
  const result = ({
    FRESH: { label: 'Онлайн', tone: 'fresh' },
    STALE: { label: 'Дані застаріли', tone: 'stale' },
    OFFLINE: { label: 'Немає зв’язку', tone: 'offline' },
  })[value] ?? { label: 'Стан невідомий', tone: 'unknown' }
  return translate ? { ...result, label: translate(`pts2UiFreshness_${result.tone}`) } : result
}
export function controllerTime(value, locale = 'uk-UA') {
  const date = value ? new Date(value) : null
  return date && Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(date)
    : '—'
}
export function configurationCaption(item, translate) {
  const label = (key, fallback) => translate ? translate(key) : fallback
  if (!item || item.configurationState === 'UNKNOWN') return label('pts2UiConfigurationMissing', 'Конфігурацію ще не отримано')
  if (item.configurationState === 'ORDER_UNCERTAIN') return label('pts2UiConfigurationOrderUnknown', 'Порядок змін конфігурації не підтверджено')
  return item.connectionFreshness === 'OFFLINE' || item.configurationState === 'HISTORICAL'
    ? label('pts2UiConfigurationHistorical', 'Остання відома конфігурація')
    : label('pts2UiConfigurationStored', 'Збережена конфігурація')
}

export function useControllerPages(controllerId = null, { requestTimeoutMs = 10000 } = {}) {
  const { t } = useI18n()
  const router = useRouter()
  const scope = computed(
    () =>
      `${session.user?.id ?? session.user?.username ?? ''}:${session.user?.accessVersion ?? ''}:network:${controllerId ?? 'overview'}`,
  )
  const stationName = computed(
    () =>
      controller.value?.stationDisplayName ?? t('pts2UiNetwork'),
  )
  const filters = reactive(controllerId ? normalizeDeviceFilters({}) : normalizeControllerFilters({}))
  const summaries = ref([])
  const summaryCursor = ref(null)
  const page = ref(null)
  const diagnostics = ref({})
  const loading = ref(true)
  const refreshing = ref(false)
  const error = ref('')
  const switching = ref(false)
  const controller = computed(() => summaries.value.find((item) => item.id === controllerId))
  const visibleControllers = computed(() => summaries.value)
  let sequence = 0
  let request
  let timer
  let debounce
  let disposed = false
  let restoring = false
  let visibleCategory = filters.category
  let selectionSequence = 0
  let selectionRequest

  const deviceQuery = () => ({ ...filters,
    search: deviceSearchQuery(filters.search, t(`pts2UiSingular_${filters.category}`)),
  })

  async function refresh({ more = false, initial = false } = {}) {
    request?.abort()
    request = new AbortController()
    const { signal } = request
    const activeRequest = request
    let timedOut = false
    const deadline = setTimeout(() => {
      timedOut = true
      activeRequest.abort()
    }, requestTimeoutMs)
    const generation = ++sequence
    const expectedScope = scope.value
    const current = () => !disposed && generation === sequence && expectedScope === scope.value
    if (!session.user) {
      summaries.value = []
      page.value = null
      diagnostics.value = {}
      loading.value = false
      clearTimeout(deadline)
      return
    }
    refreshing.value = true
    if (initial) loading.value = true
    try {
      let items = []
      let cursor = more && !controllerId ? summaryCursor.value : null
      let next
      if (controllerId) items = [await getControllerSummary(controllerId, { signal })]
      else {
        // Refresh begins at the first page; server filters apply across the authorized network.
        const targetCount = more ? 1 : Math.max(1, summaries.value.length, loadedCounts.get(expectedScope) ?? 0)
        const seenCursors = new Set()
        do {
          const result = await getNetworkControllerSummaries({ ...filters, cursor, limit: 12, signal })
          items.push(...result.items.filter((item) => !items.some((old) => old.id === item.id)))
          next = result.nextCursor
          if (!next || items.length >= targetCount) break
          if (seenCursors.has(next) || next === cursor) throw new Error('Pagination cycle')
          seenCursors.add(next)
          cursor = next
        } while (!signal.aborted)
      }
      const health = Object.fromEntries(items.map((item) => [item.id, item.diagnostics]))
      const devices = controllerId ? await getControllerDevices(items[0].stationId, controllerId, { ...deviceQuery(), signal }) : null
      if (signal.aborted) throw new DOMException('Request aborted', 'AbortError')
      if (!current()) return
      summaries.value =
        more && !controllerId
          ? [...summaries.value, ...items.filter((item) => !summaries.value.some((old) => old.id === item.id))]
          : items
      summaryCursor.value = next ?? null
      if (!controllerId) {
        loadedCounts.delete(expectedScope)
        loadedCounts.set(expectedScope, summaries.value.length)
        if (loadedCounts.size > 64) loadedCounts.delete(loadedCounts.keys().next().value)
      }
      diagnostics.value = more && !controllerId ? { ...diagnostics.value, ...health } : health
      page.value = devices
      error.value = ''
    } catch (failure) {
      if (!current() || (failure.name === 'AbortError' && !timedOut)) return
      if (denied(failure)) {
        summaries.value = []
        page.value = null
        diagnostics.value = {}
        summaryCursor.value = null
        memory.clear()
        loadedCounts.clear()
      }
      error.value = timedOut
        ? t('pts2UiTimeout')
        : denied(failure)
          ? t('pts2UiControllerDenied')
          : t('pts2UiDataRefreshError')
    } finally {
      clearTimeout(deadline)
      if (current()) {
        loading.value = false
        refreshing.value = false
      }
    }
  }

  async function moreDevices() {
    if (!page.value?.nextCursor || refreshing.value) return
    const generation = ++sequence
    const expectedScope = scope.value
    request?.abort()
    request = new AbortController()
    const activeRequest = request
    let timedOut = false
    const deadline = setTimeout(() => {
      timedOut = true
      activeRequest.abort()
    }, requestTimeoutMs)
    refreshing.value = true
    try {
      const next = await getControllerDevices(controller.value.stationId, controllerId, {
        ...deviceQuery(),
        cursor: page.value.nextCursor,
        signal: request.signal,
      })
      if (disposed || generation !== sequence || expectedScope !== scope.value) return
      page.value = { ...next, items: [...page.value.items, ...next.items] }
    } catch (failure) {
      if (generation !== sequence || disposed || (failure.name === 'AbortError' && !timedOut)) return
      if (failure.status === 400) {
        await refresh()
        return
      }
      if (denied(failure)) {
        page.value = null
        summaries.value = []
        diagnostics.value = {}
        memory.clear()
        loadedCounts.clear()
      }
      error.value = t('pts2UiMoreDevicesError')
    } finally {
      clearTimeout(deadline)
      if (generation === sequence) refreshing.value = false
    }
  }

  async function openCurrent(id) {
    const target = summaries.value.find((item) => item.id === id)
    if (!target) return
    selectionRequest?.abort()
    selectionRequest = new AbortController()
    const activeRequest = selectionRequest
    const intent = ++selectionSequence
    const contextIntentCurrent = beginContextSelection()
    switching.value = true
    const expectedScope = scope.value
    const expectedWorkingStation = session.platformContext?.activeStationId
    const current = () => !disposed && expectedScope === scope.value && intent === selectionSequence
    const deadline = setTimeout(() => activeRequest.abort(), requestTimeoutMs)
    try {
      const context = await selectControllerContext(target.stationId, id, { signal: activeRequest.signal })
      if (!current() || !contextIntentCurrent() || session.platformContext?.activeStationId !== expectedWorkingStation) return
      session.platformContext = context.platformContext
      session.pts2Context = context.pts2Context
      await router.push({ name: 'pts2-overview' })
    } catch {
      if (current()) error.value = t('pts2UiChooseError')
    } finally {
      clearTimeout(deadline)
      if (current()) switching.value = false
    }
  }

  watch(
    filters,
    () => {
      if (restoring) return
      memory.set(scope.value, { ...filters })
      request?.abort()
      sequence++
      if (controllerId && visibleCategory !== filters.category) page.value = null
      if (!controllerId) {
        loadedCounts.delete(scope.value)
        summaries.value = []
        diagnostics.value = {}
        summaryCursor.value = null
      }
      visibleCategory = filters.category
      loading.value = !page.value
      refreshing.value = true
      clearTimeout(debounce)
      debounce = setTimeout(() => refresh(), 250)
    },
    { flush: 'sync' },
  )
  watch(
    scope,
    () => {
      request?.abort()
      selectionRequest?.abort()
      selectionSequence++
      switching.value = false
      sequence++
      clearTimeout(debounce)
      summaries.value = []
      page.value = null
      diagnostics.value = {}
      summaryCursor.value = null
      restoring = true
      Object.assign(
        filters,
        controllerId
          ? normalizeDeviceFilters(memory.get(scope.value) ?? {})
          : normalizeControllerFilters(memory.get(scope.value) ?? {}),
      )
      visibleCategory = filters.category
      restoring = false
      refresh({ initial: true })
    },
    { immediate: true },
  )
  onMounted(() => {
    timer = setInterval(() => {
      if (!document.hidden && !refreshing.value && !loading.value) refresh()
    }, 5000)
  })
  onBeforeUnmount(() => {
    disposed = true
    sequence++
    request?.abort()
    selectionRequest?.abort()
    selectionSequence++
    clearInterval(timer)
    clearTimeout(debounce)
  })
  return {
    stationName,
    filters,
    summaries,
    controller,
    visibleControllers,
    summaryCursor,
    page,
    diagnostics,
    loading,
    refreshing,
    switching,
    error,
    refresh,
    moreDevices,
    openCurrent,
  }
}
